/**
 * API endpoint constants
 * Scoped by domain with lowercase keys
 *
 * Structure:
 * - auth: Core authentication (all user types)
 * - superAdmin: Owner-only endpoints (masterData, agencyManagement, advertisementManagement)
 * - agency: Agency Admin-only endpoints (maidManagement, contractManagement, interviewSchedule)
 * - maid: Maid-only endpoints (dashboard)
 * - Cross-module endpoints (userManagement, employerManagement, inboxManagement, lms)
 */
export const API_ENDPOINTS = {
    auth: {
        login: '/api/login',
        me: '/api/me',
        logout: '/api/logout',
        register: '/api/register',
        forgotPassword: '/api/password/email',
        resetPassword: '/api/password/reset',
        changePassword: '/api/change-password',
    },
    // Upload endpoints (chunked video upload)
    upload: {
        init: '/api/upload/init',
        video: '/api/upload/video',
    },
    dashboard: {
        summary: '/api/dashboard/summary',
        trafficAnalytics: '/api/dashboard/summary/traffic-analytics',
        performingAgencies: '/api/dashboard/summary/performing-agencies',
        recentInboxes: '/api/dashboard/summary/recent-inboxes',
        recentUsers: '/api/dashboard/summary/recent-users',
        // Agency dashboard endpoints
        employerInterest: '/api/dashboard/summary/employer-interest',
        performingMaids: '/api/dashboard/summary/performing-maids',
        recentContracts: '/api/dashboard/summary/recent-contracts',
    },
    superAdmin: {
        masterData: {
            skills: '/api/master-data/skills',
            generateSkillCode: '/api/master-data/skills/generate-code',
            categories: '/api/master-data/categories',
            generateCategoryCode: '/api/master-data/categories/generate-code',
            exportCategories: '/api/master-data/categories/export',
            countries: '/api/master-data/countries',
            nationalities: '/api/master-data/nationalities',
            languages: '/api/master-data/languages',
        },
        agencyManagement: {
            agencies: '/api/agency-management/agencies',
            activation: '/api/agency-management/agencies/activation',
            counter: '/api/agency-management/agencies/counter',
        },
        advertisementManagement: {
            advertisements: '/api/advertisement-management/advertisements',
            upload: '/api/advertisement-management/advertisements/upload',
            counter: '/api/advertisement-management/advertisements/counter',
        },
        subscriptionManagement: {
            // Plan endpoints
            plans: '/api/subscription-management/plans',
            planModules: '/api/subscription-management/plans/modules',
            planSummary: '/api/subscription-management/plans/summary',
            planActivation: '/api/subscription-management/plans/activation',
            // Billing submission endpoints
            submissions: '/api/subscription-management/submissions',
            submissionsCounter:
                '/api/subscription-management/submissions/counter',
            submissionsSummary:
                '/api/subscription-management/submissions/summary',
            // Subscription overview endpoints
            subscriptions: '/api/subscription-management/subscriptions',
            subscriptionsSummary:
                '/api/subscription-management/subscriptions/summary',
        },
    },
    // Cross-module endpoints (used by super-admin and potentially agency-admin)
    userManagement: {
        users: '/api/user-management/users',
        userActivation: '/api/user-management/users/activation',
        roles: '/api/user-management/roles',
        permissions: '/api/user-management/permissions',
    },
    employerManagement: {
        employers: '/api/employer-management/employers',
        removeEmployer: '/api/employer-management/employers',
        convertToEmployer: '/api/employer-management/employers',
        activation: '/api/employer-management/employers/activation',
        counter: '/api/employer-management/employers/counter',
        export: '/api/employer-management/employers/export',
    },
    inboxManagement: {
        inbox: '/api/inbox-management/inbox',
        markAsRead: '/api/inbox-management/inbox',
        counter: '/api/inbox-management/inbox/counter',
    },
    // Employer profile endpoints (Employer feature)
    employer: {
        profile: '/api/employer/profile',
        familyDetails: '/api/employer/family-details',
        preferences: '/api/employer/preferences',
        maidArchives: '/api/employer/maid-archives',
    },
    // LMS endpoints (cross-module - shared by owner and agency-admin)
    lms: {
        courses: '/api/lms/courses',
        courseDetailMaid: '/api/lms/course',
        counter: '/api/lms/courses/counter',
        uploadThumbnail: '/api/lms/courses/upload-thumbnail',
        uploadBadge: '/api/lms/courses/upload-badge-icon',
        // Dashboard endpoints
        dashboardSummary: '/api/lms/dashboard/summary',
        dashboardCourses: '/api/lms/dashboard/summary/course',
        dashboardRecentActivity: '/api/lms/dashboard/recent-activity',
    },
    // Agency endpoints (Agency Admin only)
    agency: {
        // Maid Management endpoints
        maidManagement: {
            maids: '/api/maid-management/maids',
            uploadPhoto: '/api/maid-management/maids/upload/photo',
            uploadVideo: '/api/maid-management/maids/upload/video',
        },
        // Contract Management endpoints
        contractManagement: {
            contracts: '/api/contract-management/contracts',
            counter: '/api/contract-management/contracts/counter',
            upload: '/api/contract-management/contracts/upload',
            download: '/api/contract-management/contracts',
            export: '/api/contract-management/contracts/export',
        },
        // Interview Schedule Management endpoints
        interviewSchedule: {
            interviews: '/api/maid-management/interview-schedules',
            counter: '/api/maid-management/interview-schedules/counter',
        },
        // Subscription endpoints (agency-specific)
        subscription: {
            current: '/api/subscription-management/subscriptions/current',
            submissionsUpload: '/api/subscription-management/submissions/upload',
            downloadProof: '/api/subscription-management/submissions',
            downloadInvoice: '/api/subscription-management/submissions',
        },
    },
    // Maid endpoints (Maid feature - mobile-first)
    maid: {
        dashboard: {
            courseStats: '/api/dashboard/summary/course',
            recentLearning: '/api/dashboard/summary/recent-learning',
            badges: '/api/dashboard/badges',
            myCourses: '/api/dashboard/my-course',
        },
        courses: {
            catalog: '/api/lms/maid/catalog',
            enrollments: '/api/lms/maid/enrollments',
        },
        profile: '/api/me-maid',
        changePassword: '/api/change-password-maid',
        interviews: '/api/lms/maid/interview',
        submitInterview: '/api/maid-management/interview/submit',
    },
    // Settings endpoints (all user types)
    settings: {
        companyProfile: '/api/company-profile',
        uploadProfileLogo: '/api/upload-profile',
        paymentMethods: '/api/master-data/banks',
        paymentMethodsCounter: '/api/master-data/banks/counter',
        paymentMethodsUpload: '/api/master-data/banks/upload',
        paymentMethodsActivation: '/api/master-data/banks/activation',
    },
} as const;
